import React, { useState, useEffect } from 'react';
import { Globe, RefreshCw, CheckCircle, XCircle, Database, AlertCircle, Eye } from 'lucide-react';
import axios from 'axios';

const CountriesSituationData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [approveModal, setApproveModal] = useState(null);
  const [approveCountry, setApproveCountry] = useState('');
  const [approveYear, setApproveYear] = useState('');
  const [viewModal, setViewModal] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.get('/api/geopolitical-dashboard', {
        params: { status: statusFilter, limit: 200, offset: 0 },
      });
      if (response.data?.success && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching geopolitical data:', error);
      setData([]);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleReject = async (row) => {
    setActionLoading(row.id);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`/api/geopolitical-dashboard/${row.id}/reject`);
      setMessage({ type: 'success', text: 'Rejected' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Reject failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const openApproveModal = (row) => {
    setApproveModal(row);
    setApproveCountry(row.country || '');
    setApproveYear(row.year || new Date().getFullYear());
    setMessage({ type: '', text: '' });
  };

  const handleApproveSubmit = async () => {
    if (!approveModal) return;
    const country = (approveCountry || '').trim();
    if (!country) {
      setMessage({ type: 'error', text: 'Country is required to approve' });
      return;
    }
    const yearNum = approveYear ? parseInt(approveYear, 10) : null;
    // Country in URL path so proxy cannot drop it
    const countryPath = encodeURIComponent(country);
    let url = `/api/geopolitical-dashboard/${approveModal.id}/approve/${countryPath}`;
    if (yearNum != null && !isNaN(yearNum)) url += `?year=${yearNum}`;
    setActionLoading(approveModal.id);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(url, {}, { headers: { 'Content-Type': 'application/json' } });
      setMessage({ type: 'success', text: 'Approved and added to Country Situation AI' });
      setApproveModal(null);
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Approve failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const truncate = (str, len = 80) => {
    if (!str) return '';
    return str.length <= len ? str : str.slice(0, len) + '…';
  };

  const getDisplaySource = (row) => {
    const meta = row?.source_metadata;
    if (meta && (meta.source_name || meta.publisher)) return meta.source_name || meta.publisher;
    const s = row?.source;
    if (s === 'multi_news_api') return 'Multi News API';
    if (s === 'rss') return 'RSS';
    if (s === 'gletd') return 'GLETD';
    return s || '—';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
              <Globe className="h-8 w-8 mr-3 text-primary-600" />
              Countries Situation Data
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Review items from Multiple News API, RSS, and GLETD. Approve to add to Country Situation AI.
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center ${
            message.type === 'error'
              ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
              : 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
          }`}
        >
          {message.type === 'error' ? (
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          ) : (
            <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <div className="mb-4 flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          <Database className="h-4 w-4 inline mr-1" />
          {data.length} row(s)
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Id
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    No records found.
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {row.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs">
                      <div className="truncate" title={row.title}>
                        {truncate(row.title, 100)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                      <div className="truncate" title={row.content}>
                        {truncate(row.content, 120)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-800" title={row.source_metadata?.url || row.source}>
                        {getDisplaySource(row)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewModal(row)}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          title="View details"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                        </button>
                        {row.status === 'pending' ? (
                          <>
                            <button
                              onClick={() => openApproveModal(row)}
                              disabled={actionLoading !== null}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                            >
                              {actionLoading === row.id ? '…' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleReject(row)}
                              disabled={actionLoading !== null}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              {actionLoading === row.id ? '…' : 'Reject'}
                            </button>
                          </>
                        ) : (
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              row.status === 'approved'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {row.status}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {viewModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="view-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" onClick={() => setViewModal(null)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <h3 id="view-modal-title" className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Eye className="h-5 w-5 mr-2 text-primary-600" />
                Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Title</label>
                  <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700/50 rounded-md p-3 max-h-24 overflow-y-auto">
                    {viewModal.title || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Content</label>
                  <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700/50 rounded-md p-3 max-h-64 overflow-y-auto whitespace-pre-wrap">
                    {viewModal.content || '—'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Source</label>
                  <p className="text-sm">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
                      {getDisplaySource(viewModal)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setViewModal(null)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {approveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity" onClick={() => setApproveModal(null)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Approve and add to Country Situation AI</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 truncate" title={approveModal.title}>
                {truncate(approveModal.title, 60)}
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country (required)</label>
                  <input
                    type="text"
                    value={approveCountry}
                    onChange={(e) => setApproveCountry(e.target.value)}
                    placeholder="e.g. India"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year (optional)</label>
                  <input
                    type="number"
                    value={approveYear}
                    onChange={(e) => setApproveYear(e.target.value)}
                    placeholder={new Date().getFullYear()}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setApproveModal(null)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApproveSubmit}
                  disabled={!approveCountry.trim() || actionLoading !== null}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {actionLoading === approveModal.id ? '…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountriesSituationData;
